import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  type Ref,
} from "vue";
import {
  isTipStatus,
  tipMinorUnits,
  tipScale,
  type TipCurrency,
} from "~/utils/guideTips";
import {
  TIP_RETURN_CONTEXT_STORAGE_KEY,
  tipReturnContext,
} from "~/utils/tipReturn";

export function useGuideTips(
  slug: Ref<string>,
  eligible: Ref<boolean>,
  featureEnabled: boolean,
  livemode = false,
  panelActive: Ref<boolean> = ref(true),
) {
  const { locale } = useI18n();
  const enabled = ref(false);
  const pending = ref(false);
  const error = ref("");
  const statusUnavailable = ref(false);
  const refreshing = ref(false);
  const currencies = ref<TipCurrency[]>([]);
  const currency = ref("");
  let cityId: number | undefined;
  const amountText = ref("");
  const selected = computed(() =>
    currencies.value.find((row) => row.currency === currency.value),
  );
  const amount = computed(() =>
    tipMinorUnits(amountText.value, selected.value),
  );
  const validAmount = computed(
    () =>
      amount.value !== null &&
      !!selected.value &&
      amount.value >= selected.value.minimum &&
      amount.value <= selected.value.maximum,
  );
  const canPay = computed(
    () =>
      enabled.value &&
      validAmount.value &&
      !pending.value &&
      !statusUnavailable.value,
  );
  let context = 0;
  let readVersion = 0;
  let timer: ReturnType<typeof setInterval> | undefined;
  let requestId: string | undefined;
  let lastReadAt = 0;

  watch(
    currency,
    () => {
      amountText.value = selected.value
        ? String(selected.value.default_amount / tipScale(selected.value))
        : "";
    },
    { flush: "sync" },
  );
  watch(
    [amountText, currency],
    () => {
      requestId = undefined;
      if (!statusUnavailable.value) error.value = "";
    },
    { flush: "sync" },
  );

  async function refresh() {
    const current = context;
    const version = ++readVersion;
    if (!featureEnabled || !eligible.value) {
      enabled.value = false;
      return;
    }
    refreshing.value = true;
    lastReadAt = Date.now();
    try {
      const data = await $fetch<unknown>(
        `/api/guide-tips/${encodeURIComponent(slug.value)}`,
        { retry: 0 },
      );
      if (current !== context || version !== readVersion) return;
      if (!isTipStatus(data) || data.livemode !== livemode) {
        enabled.value = false;
        throw new Error();
      }
      if (statusUnavailable.value) error.value = "";
      statusUnavailable.value = false;
      currencies.value = data.currencies;
      enabled.value = data.enabled;
      if (!selected.value || cityId !== data.city_id) {
        requestId = undefined;
        currency.value = data.enabled ? data.default_currency : "";
        amountText.value = selected.value
          ? String(selected.value.default_amount / tipScale(selected.value))
          : "";
      }
      cityId = data.city_id;
    } catch (cause) {
      if (current !== context || version !== readVersion) return;
      const status = (cause as { statusCode?: number })?.statusCode;
      if (status === 401 || status === 403 || status === 404)
        enabled.value = false;
      // Keep a previously confirmed tab and the entered amount on transient
      // failure, but require a successful refresh before enabling Pay again.
      statusUnavailable.value = true;
      error.value = "pages.guide.tips.statusUnavailable";
    } finally {
      if (current === context && version === readVersion)
        refreshing.value = false;
    }
  }

  async function checkout() {
    if (!canPay.value) return;
    const current = context;
    pending.value = true;
    error.value = "";
    requestId ??= crypto.randomUUID();
    const id = requestId;
    const payment = {
      request_id: id,
      amount: amount.value,
      currency: currency.value,
    };
    try {
      const data = await $fetch<{
        url: string;
        livemode: boolean;
        amount: number;
        currency: string;
      }>(`/api/guide-tips/${encodeURIComponent(slug.value)}`, {
        method: "POST",
        body: payment,
        retry: 0,
      });
      if (current !== context || id !== requestId) return;
      const url = new URL(data.url);
      if (
        data.livemode !== livemode ||
        data.amount !== payment.amount ||
        data.currency !== payment.currency ||
        url.origin !== "https://checkout.stripe.com" ||
        url.username ||
        url.password
      )
        throw new Error();
      const returnLocale = String(locale.value).toLowerCase().split("-")[0];
      const returnContext = tipReturnContext(returnLocale, slug.value);
      if (returnContext) {
        try {
          window.sessionStorage.setItem(
            TIP_RETURN_CONTEXT_STORAGE_KEY,
            JSON.stringify(returnContext),
          );
        } catch {
          // Storage restrictions must not prevent opening Checkout.
        }
      }
      window.location.assign(url.href);
    } catch {
      if (current === context && id === requestId) {
        error.value = "pages.guide.tips.paymentError";
        void refresh();
      }
    } finally {
      if (current === context) pending.value = false;
    }
  }
  function refreshIfStale() {
    if (
      !pending.value &&
      !refreshing.value &&
      document.visibilityState !== "hidden" &&
      Date.now() - lastReadAt >= 60000
    )
      void refresh();
  }
  onMounted(() => {
    void refresh();
    if (featureEnabled)
      timer = setInterval(() => {
        if (panelActive.value || !enabled.value) refreshIfStale();
      }, 120000);
    document.addEventListener("visibilitychange", refreshIfStale);
  });
  watch(panelActive, (active) => {
    if (active) refreshIfStale();
  });
  watch(
    [slug, eligible],
    () => {
      ++context;
      enabled.value = false;
      pending.value = false;
      refreshing.value = false;
      statusUnavailable.value = false;
      requestId = undefined;
      error.value = "";
      currency.value = "";
      cityId = undefined;
      currencies.value = [];
      void refresh();
    },
    { flush: "sync" },
  );
  onBeforeUnmount(() => {
    ++context;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", refreshIfStale);
  });
  return {
    enabled,
    pending,
    error,
    statusUnavailable,
    refreshing,
    refresh,
    checkout,
    currencies,
    currency,
    amountText,
    selected,
    validAmount,
    canPay,
  };
}
